import { useEffect } from 'react';
import { Box, Typography, Paper, Grid, TextField, Avatar, Stack, Button } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import { useTeacher } from '@/hooks/useTeacher';
import { Loader } from '@/components/feedback/Loader';
import { StatusBadge } from '@/components/feedback/StatusBadge';
import { DepartmentBadge } from '@/features/teachers/components/DepartmentBadge';

export function MyProfilePage() {
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const { teacher, loading } = useTeacher(user?.teacherId);

  if (loading || !teacher) return <Loader />;

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          My Profile
        </Typography>
        <Typography color="text.secondary">
          View your professional and contact details
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}>
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <Avatar 
              src={teacher.photo} 
              sx={{ width: 150, height: 150, fontSize: 48, bgcolor: 'primary.main' }}
            >
              {teacher.firstName.charAt(0)}
            </Avatar>
            <Button variant="outlined" size="small">Update Photo</Button>
          </Stack>
          <Box flexGrow={1} width="100%">
            <Box mb={2}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {teacher.firstName} {teacher.lastName}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {teacher.designation}
              </Typography>
              <Stack direction="row" spacing={2} mt={1}>
                <DepartmentBadge department={teacher.department} />
                <StatusBadge status={teacher.status} />
              </Stack>
            </Box>

            <Grid container spacing={3} mt={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Employee ID</Typography>
                <Typography variant="body1" fontWeight={500}>{teacher.employeeId}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                <Typography variant="body1" fontWeight={500}>{teacher.dateOfBirth}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Blood Group</Typography>
                <Typography variant="body1" fontWeight={500}>{teacher.bloodGroup}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Joining Date</Typography>
                <Typography variant="body1" fontWeight={500}>{teacher.joiningDate}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Employment Type</Typography>
                <Typography variant="body1" fontWeight={500}>{teacher.employmentType}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Experience</Typography>
                <Typography variant="body1" fontWeight={500}>{teacher.experience || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 4, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 4 }}>Contact Information</Typography>
            <Stack spacing={3}>
              <TextField 
                label="Email Address" 
                fullWidth 
                defaultValue={teacher.email}
              />
              <TextField 
                label="Phone Number" 
                fullWidth 
                defaultValue={teacher.phone}
              />
              <TextField 
                label="Emergency Contact" 
                fullWidth 
                defaultValue={teacher.emergencyContact}
              />
              <TextField 
                label="Residential Address" 
                multiline 
                rows={3} 
                fullWidth 
                defaultValue={teacher.address}
              />
              <Button variant="contained" sx={{ alignSelf: 'flex-end' }}>
                Update Contact Info
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 4, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 4 }}>Change Password</Typography>
            <Stack spacing={3}>
              <TextField 
                label="Current Password" 
                type="password"
                fullWidth 
              />
              <TextField 
                label="New Password" 
                type="password"
                fullWidth 
              />
              <TextField 
                label="Confirm New Password" 
                type="password"
                fullWidth 
              />
              <Button variant="contained" color="primary" sx={{ alignSelf: 'flex-end' }}>
                Change Password
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
